import React from 'react';
import { Select } from 'antd';
import { Icon, IconType } from '@routific/hive';
import { useTranslation } from 'react-i18next';
import DriverOption from './DriverOption';
import DriverOptionHeader from './DriverOption/DriverOptionHeader';
import { SearchBarLabel, StyledSelectDriver } from './SelectDriver.styles';
import { RouteDtoModelType, DriverAssignInfoDtoModelType } from '../../../../store/mst/models';
import hasAssignedDriver from '../helpers/hasAssignedDriver';
import SkeletonRows from './SkeletonRows';
import NotFoundContent from './NotFoundContent';
import EmptyDriver from './EmptyDriver';

const { Option } = Select;

interface Props {
  route: RouteDtoModelType;
  drivers: DriverAssignInfoDtoModelType[];
  loading: boolean;
  isPending: boolean;
  assignDriver: (driverId: string) => void;
  routeColorsByDriverId: Record<string, any>;
}

const SelectDriverView: React.FC<Props> = ({
  route,
  drivers,
  loading,
  isPending,
  assignDriver,
  routeColorsByDriverId,
}) => {
  const { t } = useTranslation('components');
  const searchBarLabel = hasAssignedDriver(route)
    ? t('assignDriver.labels.chooseDriverToReassign')
    : t('assignDriver.labels.chooseDriverToAssign');

  const handleChange = (driverId: unknown) => {
    if (typeof driverId === 'string') {
      assignDriver(driverId);
    }
  };

  const renderDropdown = (menu: any) => (
    <>
      <DriverOptionHeader />
      {loading ? <SkeletonRows /> : menu}
    </>
  );

  if (!loading && drivers.length === 0) {
    return <EmptyDriver />;
  }

  return (
    <>
      <SearchBarLabel>
        {searchBarLabel}
      </SearchBarLabel>
      <StyledSelectDriver
        showSearch
        size="middle"
        placeholder={t('assignDriver.labels.searchForDriver')}
        value={undefined}
        suffixIcon={<Icon icon={IconType.Search} size="medium" />}
        listHeight={220}
        defaultOpen
        disabled={isPending}
        getPopupContainer={trigger => trigger.closest('.assign-driver-popover')?.querySelector('.driver-list') ?? document.body}
        open
        dropdownMatchSelectWidth={false}
        bordered={false}
        status={route.driver?.deleted ? 'error' : undefined}
        onChange={handleChange}
        filterOption={(input, option) => {
          const driverName = option?.label as string;
          return driverName?.toLowerCase().includes(input?.toLowerCase());
        }}
        notFoundContent={<NotFoundContent />}
        dropdownRender={renderDropdown}
      >
        {
          !loading && (
            drivers.map((d) => {
              const routeColors = routeColorsByDriverId[d.id];
              const isActive = route.driver?.id === d.id;
              return (
                <Option value={d.id} key={d.id} label={d.name} data-cy="assign-driver-option">
                  <DriverOption driver={d} routeColors={routeColors} isActive={isActive} />
                </Option>
              );
            })
          )
        }
      </StyledSelectDriver>
    </>
  );
};

export default SelectDriverView;
